import React, { useState, ReactNode } from 'react';
import './category-list.scss';
import classNames from 'classnames';
import Icon from '../../../components/icons/icon';
import { Button } from '../../../components/form/button';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { DataTableColumn } from '../../../components/table/data-table';
import {
  Category,
  CategorySearchRequest,
  CategoryUpdateRequest,
} from '../../../clients/quipli-service/category/category';
import { toast } from '../../../util/toasts';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { SortableDataTable } from '../../../components/sortable-table/data-table';
import { QuipliLink } from '../../../components/link/quipli-link';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useCategoryApiV1 } from '../../../clients/quipli-service/category/category-api-v1';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { useCategoriesImportExportAdapter } from './categories-adapter';
import { useImportExport } from '../../../components/import-export/use-import-export';

export const CategoryList = () => {
  const { searchCategories, getMultipleById, updateCategory, updateCategoryOrder } = useCategoryApiV1();
  const [actionModal, setActionModal] = useState<ReactNode>();
  // Hoooks
  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<CategorySearchRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
    },
    async (_request) => {
      setListLoading(true);
      const categories = await searchCategories(_request);
      const parentCategories = await getMultipleById(categories.flatMap((i) => (i.parent ? [i.parent] : [])));
      setCategories(categories);
      setParentCategories(parentCategories);
      setListLoading(false);
    },
  );

  const categoriesAdapter = useCategoriesImportExportAdapter(fetchCallback);
  const { ImportExportToolbar } = useImportExport(categoriesAdapter, {}, {});

  // Vars
  const columns: DataTableColumn[] = [
    { key: 'sort', label: '', showRule: 'OnlyDesktop' },
    { key: 'RowNum', label: 'Id', showRule: 'OnlyDesktop' },
    { key: 'name', label: 'Name', showRule: 'OnlyDesktop' },
    { key: 'parent', label: 'Parent', showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'active', label: 'Active', showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'subCategories', label: 'Sub Categories', fixedAsFirst: true, showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'actionColumns', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
    { key: 'actionsMobile', label: '', fixedAsLast: true, showRule: 'OnlyMobile' },
  ];

  // Callbacks
  const buildDataRow = (category: Category, parent?: Category) => {
    return {
      mobileHeader: (
        <div className="flex gap-4">
          <Typography type="text-md" weight="bold" className={category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}>
            {category.id}
          </Typography>
          <Typography type="text-md" weight="medium">
            <NavLink
              to={`/manager/categories/form/${category.id}/0`}
              className={`underline underline-offset-1 ${
                category.isActive ? 'text-quipli-black-800' : 'text-grey-400'
              }`}
            >
              {category.name}
            </NavLink>
          </Typography>
        </div>
      ),
      fields: {
        sort: <></>,
        RowNum: (
          <>
            <div>
              <Typography
                type="text-sm"
                weight="bold"
                className={category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}
              >
                {category.id}
              </Typography>
            </div>
          </>
        ),
        name: (
          <div className="min-w-[200px]">
            <Typography type="text-sm" className={category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}>
              {category.name}
            </Typography>
          </div>
        ),
        parent: (
          <>
            <div>
              <Typography type="text-sm" className={category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}>
                {parent?.name ?? 'N/A'}
              </Typography>
            </div>
          </>
        ),
        active: (
          <>
            <div>
              <Typography type="text-sm" className={category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}>
                {category.isActive ? 'Yes' : 'No'}
              </Typography>
            </div>
          </>
        ),
        subCategories: (
          <>
            <div>
              <Typography type="text-sm" className={category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}>
                <span
                  className={`underline underline-offset-1 subCategory ${
                    category.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'
                  }`}
                  onClick={() => {
                    if (category.children?.length ?? 0 > 0) {
                      setRequest({ ...(request || {}), parentId: category.id });
                    }
                  }}
                >
                  {category.children.length}
                </span>
              </Typography>
            </div>
          </>
        ),
        actionColumns: (
          <Actions className="block" category={category} doUpdate={doUpdate} setActionModal={setActionModal} />
        ),
        actionsMobile: <ActionsMobile category={category} doUpdate={doUpdate} />,
      },
      redirectTo: `categories/form/${category.id}/0`,
    };
  };

  async function doUpdate(categoryId: number, update: CategoryUpdateRequest) {
    await updateCategory(categoryId, update)
      .then(() => {
        fetchCallback();
        toast('Successfully updated!', 'success');
        setRequest({ ...request });
      })
      .catch((err) => {
        const errMsg = err.status === 400 && err.message ? err.message.msg : 'Error updating category!';
        toast(errMsg, 'error');
      });
  }
  async function doUpdateOrder(categoryIds: number[]) {
    await updateCategoryOrder(categoryIds)
      .then(() => {
        toast('Successfully updated!', 'success');
        setRequest({ ...request });
      })
      .catch((err) => {
        const errMsg = err.status === 400 && err.message ? err.message.msg : 'Error updating category order!';
        toast(errMsg, 'error');
      });
  }

  return (
    <div className="category-list">
      <div className="container-v2">
        <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4">
          <Typography type="display-sm" weight="medium" className="text-black">
            Manage Product Categories
          </Typography>
          <ImportExportToolbar>
            <Button className="button-yellow sm" data-cy="add_category">
              <QuipliLink to="categories/form/0/0">
                <Typography
                  type="text-sm"
                  weight="bold"
                  className="flex justify-center justify-items-center gap-1 text-grey-700"
                >
                  <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
                    Add Category
                  </Typography>
                  <Icon icon="Plus" size="medium" stroke="var(--quipli-black-800)" />
                </Typography>
              </QuipliLink>
            </Button>
          </ImportExportToolbar>
        </div>

        <div className="pt-4 pb-4 flex items-center justify-between">
          <div className="w-full md:w-auto">
            <SearchBar
              data-cy="search-categories"
              placeholder="Search by keyword"
              defaultValue={request.keyword}
              setRequest={setRequest}
            />
          </div>
        </div>
        <div className="block md:hidden">
          <div className="flex pb-5">
            {request.parentId && (
              <button
                type="button"
                className="button-secondary"
                onClick={() => {
                  setRequest({});
                }}
              >
                <span className="text-[#1D252C]">Back to list</span>
              </button>
            )}
          </div>
        </div>
        <div className="pb-5 hidden md:block">
          {request.parentId && (
            <button
              type="button"
              className="button-secondary"
              onClick={() => {
                setRequest({});
              }}
            >
              <span className="text-[#1D252C]">Back to list</span>
            </button>
          )}
        </div>

        <SortableDataTable
          isLoading={isListLoading}
          columnNames={columns}
          data={categories.map((c) =>
            buildDataRow(c, c.parent ? parentCategories.find((p) => p.id === c.parent) : undefined),
          )}
          dataItems={categories}
          setNewDataItemsOrder={(newones) => {
            setCategories(newones);
            const categoryIds = newones.map((item) => item.id);
            doUpdateOrder(categoryIds);
          }}
        />
        {actionModal}
      </div>
    </div>
  );
};

const Actions = ({
  className,
  category,
  doUpdate,
  setActionModal,
}: {
  className?: string;
  category: Category;
  doUpdate: (id: number, update: CategoryUpdateRequest) => void;
  setActionModal: React.Dispatch<React.SetStateAction<ReactNode | undefined>>;
}) => {
  return (
    <div className={classNames('relative', className)}>
      <Dropdown
        placement="left-end"
        trigger={() => (
          <Button className="button-tertiary" data-cy={`customer_menu`}>
            <Icon icon="DotsVertical" />
          </Button>
        )}
      >
        <div>
          {category.isActive ? (
            <div
              className="flex gap-2"
              onClick={() => {
                doUpdate(category.id, { ...category, isActive: false });
              }}
              data-cy="disable_category"
            >
              <Icon icon="XCircle" stroke="var(--error-500)" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Disable Category
              </Typography>
            </div>
          ) : (
            <div
              className="flex gap-2"
              onClick={() => {
                doUpdate(category.id, { ...category, isActive: true });
              }}
              data-cy="enable_category"
            >
              <Icon icon="CheckCircle" stroke="var(--success-500)" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Enable Category
              </Typography>
            </div>
          )}
        </div>

        <div>
          <div
            className="flex gap-2"
            onClick={() =>
              setActionModal(
                <ConfirmModal
                  isOpen={true}
                  close={() => setActionModal(undefined)}
                  onConfirm={() => {
                    doUpdate(category.id, { ...category, isDeleted: true });
                    setActionModal(undefined);
                  }}
                  headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
                  prompt={`Are you sure you want to delete category ${category.name}?`}
                />,
              )
            }
            data-cy="delete_category"
          >
            <Icon icon="Trash" stroke="black" />
            <Typography type="text-sm" className="text-quipli-black-800">
              Delete Category
            </Typography>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};
const ActionsMobile = ({
  category,
  doUpdate,
}: {
  category: Category;
  doUpdate: (id: number, update: CategoryUpdateRequest) => void;
}) => {
  return (
    <div className="w-full pl-2 pr-2 mt-2">
      {category.isActive ? (
        <Button
          className="button-secondary w-full"
          onClick={() => {
            doUpdate(category.id, { ...category, isActive: false });
          }}
          data-cy="disable_category"
        >
          <Typography
            type="text-md"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-grey-700"
          >
            Disable category
          </Typography>
        </Button>
      ) : (
        <Button
          className="button-secondary w-full"
          onClick={() => {
            doUpdate(category.id, { ...category, isActive: true });
          }}
          data-cy="enable_category"
        >
          <Typography
            type="text-md"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-grey-700"
          >
            Enable category
          </Typography>
        </Button>
      )}
    </div>
  );
};
